import { Children, cloneElement, createContext, isValidElement, useContext, useId, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import type { MDXComponents } from 'mdx/types';
import { CaretDown, Check, Minus } from '@phosphor-icons/react';

import { Note, Section, SmartLink } from '../page';
import { InlineCta, signupFrom } from '../blog/Figure';
import { AgentSuspend, ContextCost, LaptopLeak, SplitCalculator, Switchboard } from '../blog/AccessWidgets';
import { AttackerLanding, EgressDemo, ReleaseGate, RequestPath } from '../blog/DefenseWidgets';
import { AgentIdentityLanes, AuditLogFeed, PermissionCheck } from '../guide/GuideFigures';
import { GatewayChooser, GatewayCriteria, TeamCostCompare } from '../guide/GatewayChoiceFigures';
import { ProxyKinds } from '../guide/ProxyKinds';
import { ProxyLayers } from '../guide/ProxyLayers';
import { ProxyOrGateway } from '../guide/ProxyOrGateway';
import { SiteLink } from '../SiteLink';
import { linkProps } from '../../lib/links';

/**
 * The tags MDX files in src/content can use, with no imports in the file.
 * Each one renders the same markup as the TSX page pieces (components/page),
 * so an MDX page looks exactly like a hand-built one.
 *
 * Writing guide: src/content/README.md.
 */

// ─── Rows, steps, FAQ ───────────────────────────────────────────────────────

/** Hairline rows. `one` puts them in a single column. */
function Rows({ one, children }: { one?: boolean; children: ReactNode }) {
  return <ul className={one ? 'pg-rows one' : 'pg-rows'}>{children}</ul>;
}

function Row({ title, children }: { title: string; children: ReactNode }) {
  return (
    <li>
      <h3>{title}</h3>
      <div className="pg-row-body">{children}</div>
    </li>
  );
}

/** Numbered steps. Number them by order; the numerals are added here. */
function Steps({ children }: { children: ReactNode }) {
  let n = 0;
  return (
    <ol className="pg-steps">
      {Children.map(children, (c) =>
        isValidElement(c) ? cloneElement(c as ReactElement<{ n?: number }>, { n: ++n }) : null,
      )}
    </ol>
  );
}

function Step({ title, n, children }: { title: string; n?: number; children: ReactNode }) {
  return (
    <li>
      <span className="pw-n" aria-hidden="true">
        {n}
      </span>
      <div>
        <h3>{title}</h3>
        <div className="pg-row-body">{children}</div>
      </div>
    </li>
  );
}

function FAQ({ children }: { children: ReactNode }) {
  return <div>{children}</div>;
}

/** One question. Answers stay in the HTML while closed, like the pricing FAQ. */
export function Q({ q, children }: { q: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const answerId = `faq-${useId().replace(/:/g, '')}`;
  return (
    <div className="l-faq-item">
      <h3>
        <button
          type="button"
          className="l-faq-q"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={answerId}
        >
          {q}
          <span className="s-caret" aria-hidden="true">
            <CaretDown size={18} weight="bold" />
          </span>
        </button>
      </h3>
      <div id={answerId} className="x-collapse" data-open={open || undefined}>
        <div>
          <div className="l-faq-a">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Tables ─────────────────────────────────────────────────────────────────

/**
 * Wrap a markdown table to style it as a comparison table. `highlight` is the
 * column to tint (0 is the row labels), `caption` is read by screen readers.
 * A cell that is exactly Yes or No renders as a check or a dash.
 */
const TableCtx = createContext<{ highlight?: number; caption?: string }>({});

function Table({ caption, highlight, children }: { caption: string; highlight?: number; children: ReactNode }) {
  return (
    <TableCtx.Provider value={{ highlight, caption }}>
      <div className="pg-table-wrap">{children}</div>
    </TableCtx.Provider>
  );
}

function MdTable({ children }: { children?: ReactNode }) {
  const { caption } = useContext(TableCtx);
  return (
    <table className="pg-table">
      {caption && <caption className="sr-only">{caption}</caption>}
      {children}
    </table>
  );
}

function Tr({ children }: { children?: ReactNode }) {
  let col = 0;
  return (
    <tr>
      {Children.map(children, (c) =>
        isValidElement(c) ? cloneElement(c as ReactElement<{ col?: number }>, { col: col++ }) : c,
      )}
    </tr>
  );
}

function cell(children: ReactNode) {
  if (children === 'Yes')
    return (
      <>
        <Check size={16} weight="bold" aria-hidden="true" className="pg-yes" />
        <span className="sr-only">Yes</span>
      </>
    );
  if (children === 'No')
    return (
      <>
        <Minus size={16} aria-hidden="true" className="pg-no" />
        <span className="sr-only">No</span>
      </>
    );
  return children;
}

function Th({ col, children }: { col?: number; children?: ReactNode }) {
  const { highlight } = useContext(TableCtx);
  return (
    <th scope="col" className={col === highlight ? 'hi' : undefined}>
      {children}
    </th>
  );
}

function Td({ col, children }: { col?: number; children?: ReactNode }) {
  const { highlight } = useContext(TableCtx);
  if (col === 0) return <th scope="row">{cell(children)}</th>;
  return <td className={col === highlight ? 'hi' : undefined}>{cell(children)}</td>;
}

// ─── Figures ────────────────────────────────────────────────────────────────

/**
 * A figure: the animation first, then the caption as ordinary markdown
 * (links and bold work). `caption="..."` also works for a plain one.
 */
function Fig({ caption, children }: { caption?: string; children: ReactNode }) {
  const parts = Children.toArray(children);
  const isText = (c: ReactNode) => isValidElement(c) && c.type === 'p';
  const body = parts.filter((c) => !isText(c));
  const text = parts.filter(isText).map((c) => (c as ReactElement<{ children: ReactNode }>).props.children);
  return (
    <figure className="bl-fig">
      {body}
      {(caption || text.length > 0) && <figcaption>{caption ?? text}</figcaption>}
    </figure>
  );
}

/** A pulled quote with its source. */
function Quote({ source, href, children }: { source: string; href: string; children: ReactNode }) {
  return (
    <blockquote>
      {children}
      <cite>
        <a href={href} target="_blank" rel="noopener noreferrer">
          {source}
        </a>
      </cite>
    </blockquote>
  );
}

/** The closing call to action of a post: sign up, or download the app. */
function PostCta({ refTag, title, children }: { refTag: string; title: string; children: ReactNode }) {
  return (
    <div className="bl-cta">
      <p>
        <b>{title}</b> {children}
      </p>
      <div className="bw-btns">
        <a className="s-btn s-btn-pri" href={signupFrom(refTag)} target="_blank" rel="noopener noreferrer">
          Start free
        </a>
        <SiteLink className="s-btn s-btn-sec" to="/download">
          Download the app
        </SiteLink>
      </div>
    </div>
  );
}

const shared = {
  Section,
  Rows,
  Row,
  Steps,
  Step,
  FAQ,
  Q,
  Note,
  Table,
  Fig,
  Quote,
  InlineCta,
  PostCta,
  table: MdTable,
  tr: Tr,
  th: Th,
  td: Td,
  // Animated figures. A new one is a component in components/blog or
  // components/guide, added here.
  PermissionCheck,
  AgentIdentityLanes,
  AuditLogFeed,
  Switchboard,
  SplitCalculator,
  ContextCost,
  AgentSuspend,
  LaptopLeak,
  RequestPath,
  AttackerLanding,
  EgressDemo,
  ReleaseGate,
  GatewayCriteria,
  GatewayChooser,
  TeamCostCompare,
  ProxyKinds,
  ProxyLayers,
  ProxyOrGateway,
};

/** Tags for pages: links get the house link style. */
export const pageComponents: MDXComponents = {
  ...shared,
  a: ({ href = '', children }) => <SmartLink href={href}>{children}</SmartLink>,
};

/**
 * Tags for posts. Links are styled by .bl-body, so they carry no class, and
 * bold is <b> because the blog styles (figure captions, calls to action)
 * target b.
 */
export const postComponents: MDXComponents = {
  ...shared,
  a: ({ href = '', children }) => {
    const p = linkProps(href);
    if (p.to) return <SiteLink to={p.to}>{children}</SiteLink>;
    return (
      <a href={p.href} target={p.external ? '_blank' : undefined} rel={p.external ? 'noopener noreferrer' : undefined}>
        {children}
      </a>
    );
  },
  strong: ({ children }) => <b>{children}</b>,
};
